import { getFlareAttr, updateFlareNodeAttrs } from '@common/flare/prosemirror/transform/node';
import { CollabSchema } from '@common/prosemirror/model/collab-schema';
import { deepestBlocksBetween, FoundNodeInfo, rangeNode } from '@common/prosemirror/model/node-find';
import { selectedTableNodes } from '@common/prosemirror/model/table';
import { getTrackedChangesState, TrackedChangesPluginState, TrackedChangesTrackedMetaKey } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { restoreNodeSelection, selectNodeContent } from '@common/prosemirror/state/transaction';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { applyChangeToNodeAttrs, copyNodeWithChanges } from '@common/prosemirror/tracked-changes/copy';
import { TrackedChangeNodeAttrs } from '@common/prosemirror/tracked-changes/tracked-change-node-attrs.type';
import { getTrackedChangeNodeAttrs, getTrackedChangeUser, removeTrackedChangesFromNodeAttrs } from '@common/prosemirror/tracked-changes/tracked-changes';
import { inlineRangeForWrapping, isInlineRange, wrapInlineRange } from '@common/prosemirror/transform/inline-range';
import { unbind } from '@common/prosemirror/transform/node';
import { canChangeType, clearIncompatible, replaceLinebreaks, replaceNewlines } from '@common/prosemirror/transform/transform.prosemirror';
import { getNodeTypeByTagName, getSourceTagName } from '@common/util/schema';
import { Attrs, NodeType, ProseMirrorNode, ResolvedPos } from 'prosemirror-model';
import { AllSelection, EditorState, NodeSelection, TextSelection, Transaction } from 'prosemirror-state';
import { CellSelection } from 'prosemirror-tables';
import { Mapping } from 'prosemirror-transform';
import { EditorView } from 'prosemirror-view';

export interface SelectionStyle {
  isInlineSelection?: boolean;
  isInlineNodeSelection?: boolean;
  selectedIds: string[];
  selectedStyleName: string;
  selectedStyles: string[];
  selectedTags: string[];
}

export interface TrackedChangesOptions {
  trackedChangesState: TrackedChangesPluginState,
  // Keep all the add and remove changes grouped together by using the same id
  addTrackedChanges: TrackedChangeNodeAttrs,
  removeTrackedChanges: TrackedChangeNodeAttrs
}

/**
 * Editor command which applies a style to the document selection.
 */
export declare type SetSelectionFormatCommand = (state: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView, tagAndClass?: string) => boolean;

/**
 * Gets the style applied to the document selection.
 */
export function getSelectionStyle(state: EditorState): SelectionStyle {
  const selection = state.selection;
  const { $from, $to, empty } = selection;
  const doc = $from.doc;

  let isInlineSelection = false;
  let isInlineNodeSelection = false;
  let selectedIds: string[] = [];
  let selectedTags: string[] = [];
  let selectedStyles: string[] = [];
  let selectedStyleName: string = null;

  let selectedNodes: FoundNodeInfo[];
  if (selection instanceof AllSelection) {
    // all document is selected, so read style from <html> node
    selectedNodes = [{ node: doc.firstChild, pos: 1 }];
  } else if (selection instanceof NodeSelection) {
    // specific node is selected, so read style from this node
    selectedNodes = [{ node: selection.node, pos: $from.pos }];
    isInlineNodeSelection = isInlineWithInlineContent(selection.node.type);
  } else if (empty || !isInlineRange($from, $to)) {
    // if is empty selection or blocks are selected, then read styling from the selected blocks
    selectedNodes = blocksForStyling(state);
  } else {
    // otherwise get applied styles from selected inline range
    const range = inlineRangeForWrapping($from, $to);
    const node = rangeNode(range.$from, range.$to);
    if (node && !node.isText) {
      const pos = range.$from.pos;
      selectedNodes = [{ node, pos }];
      isInlineNodeSelection = isInlineWithInlineContent(node.type);
    } else {
      isInlineSelection = true;
      selectedStyleName = '(text)';
    }
  }

  if (selectedNodes) {
    selectedIds = Array.from(new Set(selectedNodes.map(info => nodeId(info.node)))).filter(id => !!id); // unique node IDs
    selectedTags = Array.from(new Set(selectedNodes.map(info => getSourceTagName(info.node)))); // unique node tags
    selectedStyles = Array.from(new Set(selectedNodes.map(info => nodeStyleName(info.node)))); // unique node styles
    if (selectedStyles.length === 1)
      selectedStyleName = selectedStyles[0];
    if (selectedStyles.length > 1)
      selectedStyleName = '(multiple)';
  }

  return { isInlineSelection, isInlineNodeSelection, selectedIds, selectedStyleName, selectedStyles, selectedTags };
}

/**
 * Returns the nodes the apply-style command would target for the current selection, mirroring the
 * branching used by setSelectionFormat / getSelectionStyle. Returns null when the selection is pure
 * inline text (no specific node target — the command would wrap a span).
 */
function getFormatTargetNodes(state: EditorState): FoundNodeInfo[] | null {
  const selection = state.selection;
  if (!selection) return null;
  const { $from, $to, empty } = selection;
  const doc = $from.doc;

  if (selection instanceof AllSelection) {
    return [{ node: doc.firstChild, pos: 1 }];
  }
  if (selection instanceof NodeSelection) {
    return [{ node: selection.node, pos: $from.pos }];
  }
  if (empty || !isInlineRange($from, $to)) {
    return blocksForStyling(state);
  }
  const range = inlineRangeForWrapping($from, $to);
  const node = rangeNode(range.$from, range.$to);
  if (node && !node.isText) {
    return [{ node, pos: range.$from.pos }];
  }
  return null;
}

/**
 * Applies given style to the document selection.
 */
export function setSelectionFormat(): SetSelectionFormatCommand {
  return (state: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView, tagAndClass?: string): boolean => {
    // Bail out if any target node opts out of styling (HTML head elements, comments, etc.) so the
    // toolbar can disable the apply-style button via the standard dry-run check.
    if (getFormatTargetNodes(state)?.some(info => info.node.type.spec.noStyles)) {
      return false;
    }

    if (dispatch) {
      const selection = state.selection;
      if (!selection)
        return false;

      const trackedChangesState = getTrackedChangesState(state);
      const { $from, $to, empty } = selection;
      const tr = state.tr;
      if (selection instanceof AllSelection) {
        // all document is selected, so set style to <html> node
        toggleNodeFormat(tr, tr.doc.firstChild, 0, tagAndClass, trackedChangesState);
      } else if (selection instanceof NodeSelection) {
        // specific node is selected, so read style from this node
        toggleNodeFormat(tr, selection.node, $from.pos, tagAndClass, trackedChangesState);
        if (tagAndClass === null) {
          restoreNodeSelection(tr, selection.node, $from.pos);
        } else {
          tr.setSelection(NodeSelection.create(tr.doc, $from.pos));
        }
      } else if (empty || !isInlineRange($from, $to)) {
        // if is empty selection or blocks are selected, then read styling from the selected blocks
        const selectedNodes = blocksForStyling(state);
        toggleBlockNodesFormat(tr, selectedNodes, tagAndClass, trackedChangesState);

        if (tr.docChanged) {
          let fromMapped = tr.mapping.map($from.pos, -1);
          let toMapped = tr.mapping.map($to.pos, -1);
          // Correct mappedFrom position in the first node was wrapped
          const doc = tr.docs[0] ?? tr.doc;
          const $fromResolved = doc.resolve($from.pos);
          const $fromMappedResolved = tr.doc.resolve(fromMapped);
          if ($fromMappedResolved.textOffset === 0 && $fromMappedResolved.textOffset !== $fromResolved.textOffset) {
            fromMapped += 2 + $from.textOffset;
          }
          // Correct mappedTo position in the first node was wrapped
          const $toResolved = doc.resolve($to.pos);
          const $toMappedResolved = tr.doc.resolve(toMapped);
          if ($toMappedResolved.textOffset === 0 && $toMappedResolved.textOffset !== $toResolved.textOffset) {
            toMapped += 2 + $to.textOffset;
          }
          // Restore the selection after changes
          tr.setSelection(TextSelection.create(tr.doc, fromMapped, toMapped));
        }
      } else {
        // otherwise get applied styles from selected inline range
        const range = inlineRangeForWrapping($from, $to);
        const node = rangeNode(range.$from, range.$to);
        if (node && !node.isText) {
          toggleNodeFormat(tr, node, range.$from.pos, tagAndClass, trackedChangesState);
        } else if (tagAndClass) {
          toggleInlineFormat(tr, range.$from, range.$to, tagAndClass, trackedChangesState, (node, pos) => {
            // select the newly created node content
            selectNodeContent(tr, node, pos);
          });
        }
      }
      if (tr.docChanged) {
        if (trackedChangesState?.enabled) {
          tr.setMeta(TrackedChangesTrackedMetaKey, true);
        }
        if (!(selection instanceof AllSelection)) {
          tr.scrollIntoView();
        }
        dispatch(tr);
      }
    }
    return true;
  }
}

/**
 * Applies the style to the specified inline range.
 * @param tr Transaction to apply the changes to
 * @param $from Start position of the range to apply style to
 * @param $to End position of the range to apply style to
 * @param tagAndClass Tag and class to apply
 * @param trackedChangesState State of the tracked changes plugin
 */
function toggleInlineFormat(tr: Transaction, $from: ResolvedPos, $to: ResolvedPos, tagAndClass: string, trackedChangesState: TrackedChangesPluginState, out?: (node: ProseMirrorNode, pos: number) => void): void {
  let { tag, tagClass } = parseTagAndClass(tagAndClass);
  tag = tag || 'span'; // default to span if no tag is specified
  const schema = tr.doc.type.schema;
  const nodeType = schema.nodes[tag];
  if (!nodeType || !nodeType.isInline)
    return;
  let attrs = { class: tagClass ? tagClass : undefined };
  if (trackedChangesState?.enabled) {
    const trackedChangeNodeAttrs = trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Bind, {
      changeId: trackedChangesState.getParentChangeId($from, ChangeType.Bind)
    });
    attrs = {
      ...attrs,
      ...trackedChangeNodeAttrs,
    }
  }
  // wrap inline content in the specified tag
  wrapInlineRange(tr, { $from, $to }, nodeType, attrs, out);
}

/** Applies format to the specified array of block nodes.
 * @param tr Transaction to apply the changes to
 * @param nodes Array of nodes with their mapped positions to apply the formatting to
 * @param tagAndClass Tag and class to apply
 * @param trackedChangesState State of the tracked changes plugin
 */
function toggleBlockNodesFormat(tr: Transaction, nodes: FoundNodeInfo[], tagAndClass: string, trackedChangesState: TrackedChangesPluginState): void {
  if (!nodes?.length || tagAndClass === null) {
    return;
  }
  const { tag, tagClass } = parseTagAndClass(tagAndClass);
  // If any node has a different tag name than the applied one, then will change the node type for all nodes, otherwise just update the class.
  // This is needed to properly update the styles when the same style with different tags is applied to multiple selected nodes.
  const changeType = tag && nodes.some(item => {
    const nodeTag = getSourceTagName(item.node);
    return nodeTag !== tag; // check if all nodes have the same tag name as the applied one
  });

  if (changeType) {
    const schema = tr.doc.type.schema;
    const nodeType = tag ? getNodeTypeByTagName(schema, tag) : null;
    if (tag && !nodeType) {
      throw new Error(`Node type with tag name '${tag}' is not defined in the schema.`);
    }
    setNodesType(tr, nodes, nodeType, tagClass, trackedChangesState);
  } else {
    nodes.forEach(item => {
      setTagClass(tr, item.node, item.pos, tagClass, trackedChangesState);
    });
  }
}


/**
 * Applies format to the specified node.
 * @param tr Transaction to apply the changes to
 * @param node Node to apply the style to, it used to check tag name and existing classes
 * @param pos The mapped position of the node in the transaction
 * @param tagAndClass Tag and class to apply, if null, then try to unwrap the nodes
 * @param trackedChangesState State of the tracked changes plugin
 */
function toggleNodeFormat(tr: Transaction, node: ProseMirrorNode, pos: number, tagAndClass: string, trackedChangesState: TrackedChangesPluginState): void {
  // if tagAndClass is null, then try to unwrap the node
  if (tagAndClass === null) {
    unbindInlineNode(tr, node, pos, trackedChangesState);
    return;
  }
  const { tag, tagClass } = parseTagAndClass(tagAndClass);
  const nodeTag = getSourceTagName(node);
  // If the node tag is different from the applied tag, then try to change the node type, otherwise just update the class
  if (tag && tag !== nodeTag) {
    const schema = tr.doc.type.schema;
    const nodeType = getNodeTypeByTagName(schema, tag);
    if (!nodeType) {
      throw new Error(`Node type with tag name '${tag}' is not defined in the schema`);
    }
    setNodesType(tr, [{ node, pos }], nodeType, tagClass, trackedChangesState);
  } else {
    setTagClass(tr, node, pos, tagClass, trackedChangesState);
  }
}

/** Applies the specified node type and class to the nodes.
 * @param tr Transaction to apply the changes to
 * @param items Array of nodes with their mapped positions to apply the type and class to
 * @param nodeType Node type to apply, if null, then node type is not changed
 * @param tagClass Class to apply, if empty or null, then all classes are removed
 * @param trackedChangesState State of the tracked changes plugin
*/
function setNodesType(tr: Transaction, items: FoundNodeInfo[], type: NodeType, tagClass: string, trackedChangesState: TrackedChangesPluginState): void {
  const schema = tr.doc.type.schema;
  // Configure attributes for the node to update CSS class and remove direct styles.
  const attrs = { class: tagClass ? tagClass : undefined, style: undefined };
  // Get a new instance of the tracked change options to create the own groups of the add and remove changes for each set of items that are changed together,
  // since this method can be called multiple times during the current transaction.
  const options: TrackedChangesOptions = getTrackedChangesOptions(trackedChangesState);

  const changedItems: FoundNodeInfo[] = [];
  const mapFrom: number = tr.steps.length;

  let startM: number;
  items.forEach(item => {
    const { node, pos } = item;
    let attrsHere: Attrs = attrs;

    // If tracked changes are enabled then return the node's attributes with the tracked change applied
    if (options?.trackedChangesState?.enabled) {
      attrsHere = {
        ...node.attrs,
        ...attrsHere,
        ...applyChangeToNodeAttrs(getTrackedChangeNodeAttrs(options?.trackedChangesState.changeMarkType, node), options.addTrackedChanges.changeList[0])
      };
    }
    // Else remove existing tracked changes from the node's existing attributes
    else {
      attrsHere = removeTrackedChangesFromNodeAttrs({
        ...node.attrs,
        ...attrsHere
      });
    }

    if (!node.hasMarkup(type, attrsHere) && canChangeType(tr.doc, tr.mapping.slice(mapFrom).map(pos), type) && !attrsHere?.changeList?.some(change => change.changeType === ChangeType.Remove)) {
      let convertNewlines = null;
      const wrapInBlock = node.type.spec.content?.includes('inline*') && type.spec.content?.includes('block+');

      if (!wrapInBlock) {
        if (schema.linebreakReplacement) {
          const pre: boolean = type.whitespace === 'pre';
          const supportLinebreak: boolean = !!type.contentMatch.matchType(schema.linebreakReplacement);
          if (pre && !supportLinebreak) {
            convertNewlines = false;
          } else if (!pre && supportLinebreak) {
            convertNewlines = true;
          }
        }

        // Ensure all markup that isn't allowed in the new node type is cleared
        if (convertNewlines === false) {
          replaceLinebreaks(tr, node, pos, mapFrom);
        }
        clearIncompatible(tr, tr.mapping.slice(mapFrom).map(pos, 1), type, undefined, convertNewlines === null);
      }

      const mapping: Mapping = tr.mapping.slice(mapFrom);
      startM = mapping.map(pos, 1);
      let endM: number = mapping.map(pos + node.nodeSize, 1);

      if (wrapInBlock) {
        // If tracked changes are enabled then apply the add changes to the node
        const nodeToWrap = options?.trackedChangesState?.enabled
          ? copyNodeWithChanges(options?.trackedChangesState.changeMarkType, node, options.addTrackedChanges)
          : node;
        // Create a special node to wrap the original inline content, since changing type of the original node with inline content to block will not be allowed.
        const inlineContentContainer = schema.nodes.mcCentralContainer.create(null, nodeToWrap.content);
        // Create a new block node with the specified type and the wrapper as its content
        const blockNode = type.create(attrsHere, inlineContentContainer, nodeToWrap.marks);
        // Replace the original node with the new block node
        tr.replaceRangeWith(startM, endM, blockNode);
      } else {
        // If tracked changes are enabled then apply the add changes to the textblock
        if (options?.trackedChangesState?.enabled) {
          tr.replaceRangeWith(startM, endM, copyNodeWithChanges(options?.trackedChangesState.changeMarkType, node, options.addTrackedChanges));
        }

        tr.setNodeMarkup(startM, type, attrsHere, node.marks);
      }

      // Collect changed nodes and steps to be able to insert tracked removals after the loop
      changedItems.push(item);

      if (convertNewlines === true) {
        replaceNewlines(tr, node, pos, mapFrom);
      }
    }
  });

  // Insert a tracked removal of the changed nodes after the range
  if (options?.trackedChangesState?.enabled) {
    // Get the steps in the current selection range
    let trackedDeleteGroup: ProseMirrorNode[] = [];
    for (let i = 0; i < changedItems.length; i++) {
      const item = changedItems[i];

      trackedDeleteGroup.push(changedItems[i].node);
      // Group deletes with adjacent wrap steps together
      if (item.pos + item.node.nodeSize !== changedItems[i + 1]?.pos) {
        // When the next step is not adjacent, insert the group of tracked delete changes
        let insertPos: number = tr.mapping.slice(mapFrom).map(item.pos + item.node.nodeSize);

        for (const node of trackedDeleteGroup) {
          tr.insert(insertPos, copyNodeWithChanges(options?.trackedChangesState.changeMarkType, node, options.removeTrackedChanges));
          // Move the insert position past the node that was inserted
          insertPos += node.nodeSize;
        }

        trackedDeleteGroup = [];
      }
    }
  }
}

/** Applies the specified tag class to the node. If tag class is empty, then removes all classes from the node.
 * @param tr Transaction to apply the changes to
 * @param node Node to apply the style to, it used to check existing classes
 * @param pos The mapped position of the node in the transaction
 * @param tagClass Class to apply
 * @param trackedChangesState State of the tracked changes plugin
*/
function setTagClass(tr: Transaction, node: ProseMirrorNode, pos: number, tagClass: string, trackedChangesState: TrackedChangesPluginState): void {
  // Skip processing removed nodes
  if (node.attrs.changeList?.some(change => change.changeType === ChangeType.Remove)) {
    return;
  }
  const attrs = {};
  let updateNode = false;

  const nodeClassNames = classNames(node);
  // Apply new class if not equal to the existing one. If tagClass is empty, then remove all classes from the node.
  if (tagClass && (nodeClassNames?.length !== 1 || nodeClassNames[0] !== tagClass) || (!tagClass && nodeClassNames?.length)) {
    attrs['class'] = tagClass ? tagClass : undefined;
    updateNode = true;
  }

  // When applying class-based style, remove any inline styles to avoid potential style conflicts
  if (getFlareAttr(node.attrs, 'style')) {
    attrs['style'] = undefined;
    updateNode = true;
  }

  if (updateNode) {
    // apply the updated attributes to the node
    updateFlareNodeAttrs(tr, node, pos, attrs, trackedChangesState, { mergeStyles: false });
  }
}

/** Unbinds the specified inline node to remove its formatting.
 * @param tr Transaction to apply the changes to
 * @param node Node to unbind, it used to check if the node is inline and has inline content
 * @param pos The mapped position of the node in the transaction
 * @param trackedChangesState State of the tracked changes plugin
*/
function unbindInlineNode(tr: Transaction, node: ProseMirrorNode, pos: number, trackedChangesState: TrackedChangesPluginState): void {
  // Only allow unbinding of inline nodes with inline content.
  if (!isInlineWithInlineContent(node.type)) {
    return;
  }
  if (!trackedChangesState?.enabled) {
    unbind(tr, node, pos, (left) => left.node.isInline);
    return;
  }
  const schema = tr.doc.type.schema;

  // Resolve the wrapper position before unbinding so we can mark the same content after it is lifted.
  const rangeFrom = tr.mapping.map(pos);
  const rangeTo = rangeFrom + node.content.size;

  // Skip the unbind mark when the current user is removing a wrapper they just added as a tracked bind.
  const trackedChangeNodeAttrs = getTrackedChangeNodeAttrs(trackedChangesState.changeMarkType, node);
  const currentUserId = getTrackedChangeUser(trackedChangesState.user)?.Id;
  const ignoreUnbindTrack = trackedChangeNodeAttrs?.changeList?.some(change => change.changeType === ChangeType.Bind && change.creatorCentralUserId === currentUserId);

  unbind(tr, node, pos, (left) => left.node.isInline);

  if (!ignoreUnbindTrack) {
    // Serialize the original wrapper without tracked attrs so the unbind record stores clean XML.
    const attrsWithoutTrackedChanges = removeTrackedChangesFromNodeAttrs(node.attrs);
    const originalNode = node.type.create(attrsWithoutTrackedChanges, null, node.marks);
    const unboundNodeXML = (schema as CollabSchema).nodeToCode(originalNode);
    const unboundAttrs = trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Unbind, { changeContent: unboundNodeXML });

    // Mark the lifted content so tracked changes show where the wrapper was removed.
    tr.addMark(rangeFrom, rangeTo, trackedChangesState.changeMarkType.create(unboundAttrs));
  }
}

/** Parses tag and class name from the specified value. */
function parseTagAndClass(tagAndClass: string): { tag: string, tagClass: string } {
  const parts = tagAndClass.split('.');
  return {
    tag: parts[0],
    tagClass: parts.length > 1 ? parts[1] : null
  };
}

/** Get options for tracking the adding and removing changes. */
function getTrackedChangesOptions(trackedChangesState: TrackedChangesPluginState): TrackedChangesOptions {
  return {
    trackedChangesState: trackedChangesState,
    addTrackedChanges: trackedChangesState?.user ? trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Add) : null,
    removeTrackedChanges: trackedChangesState?.user ? trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Remove) : null
  };
}

/** Checks if the specified node type is inline and allows inline content. */
function isInlineWithInlineContent(type: NodeType): boolean {
  return type.isInline && type.inlineContent;
}

/** Returns node ID from the specified node. */
function nodeId(node: ProseMirrorNode): string {
  return getFlareAttr(node.attrs, 'id');
}

/** Returns a style name for the node in css selector format: tag[#nodeID][.class1][.class2]..[.classN] */
function nodeStyleName(node: ProseMirrorNode): string {
  let styleName = getSourceTagName(node);
  const nodeId = getFlareAttr(node.attrs, 'id');
  if (nodeId) {
    styleName += `#${nodeId}`;
  }
  const classes = classNames(node);
  if (classes && classes.length) {
    styleName += `.${classes.join('.')}`;
  }
  return styleName;
}

/** Gets an array of class names from the specified node. */
function classNames(node: ProseMirrorNode): string[] | null {
  const classes = getFlareAttr<string>(node, 'class');
  return classes ? classes.split(' ') : null;
}

/** Gets block nodes from the given range to read/apply styling. */
function blocksForStyling(state: EditorState): FoundNodeInfo[] {
  const sel = state.selection;
  if (sel instanceof CellSelection) {
    return selectedTableNodes(state);
  }
  const { $from, $to } = sel, forbidden = forbiddenTypes(state);
  return deepestBlocksBetween($from, $to, node => !forbidden.has(node.type));
}

/** Some of node types are forbidden to have styling. */
function forbiddenTypes(state: EditorState): Set<NodeType> {
  const nodeTypes = state.schema.nodes;
  return new Set([
    nodeTypes.mcCentralContainer,
    // do not allow to apply styling to table col nodes for text selection
    nodeTypes.col,
    nodeTypes.colgroup
  ]);
}
